import { useEffect, useState } from 'react'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import {
  hideRightPanel,
  selectRightPanelVisibility,
  showRightPanel,
} from 'features/unifiedFlow/store/unifiedFlowSlice'

import CampaignLogs from '../../components/CampaignLogs'
import RightPanel from '../../components/layout/RightPanel'
import {
  getContentHasVariants,
  getIsOptimized,
  getIsPersonalized,
} from '../../helpers'
import { useContent, useSelectedElement } from '../../hooks'
import { useVariantInsights } from '../context/VariantInsightsContext'

import CollapsedIcons from './CollapsedIcons'
import ExpandedIcons from './ExpandedIcons'
import ExperimentSettings from './experimentSettings'
import InsightsVariantManager from './InsightsVariantManager'

export type ActivePanel =
  | 'variantInspector'
  | 'advancedSettings'
  | 'insights'
  | 'campaignLogs'
export type HandleActivePanelChange = (panel: ActivePanel) => void

const RightSection = () => {
  const flags = useFlags()

  const dispatch = useAppDispatch()
  const [activePanel, setActivePanel] =
    useState<ActivePanel | undefined>(undefined)

  const { content } = useContent()
  const { data: selectedElement } = useSelectedElement()
  const rightPanelVisibility = useAppSelector(selectRightPanelVisibility)
  const { state, clearVariantState } = useVariantInsights()

  const isOptimized = getIsOptimized(selectedElement)
  const isPersonalized = getIsPersonalized(selectedElement)
  const hasVariants = getContentHasVariants(content)

  const handleActivePanelChange: HandleActivePanelChange = (
    panel: ActivePanel
  ) => {
    if (panel === activePanel) {
      setActivePanel(undefined)
      dispatch(hideRightPanel())
    } else {
      setActivePanel(panel)
      if (activePanel === 'variantInspector') {
        clearVariantState()
      }
      if (rightPanelVisibility !== 'expanded') {
        dispatch(showRightPanel())
      }
    }
  }

  useEffect(() => {
    if (state.isVariantSelected) {
      setActivePanel('variantInspector')
    } else if (!state.isVariantSelected && activePanel === 'variantInspector') {
      setActivePanel('insights')
    }
  }, [activePanel, state.isVariantSelected])

  useEffect(() => {
    const shouldHidePanelDefault = !hasVariants
    const shouldHideRightPanel = flags.showSentimentsMapMockData
      ? shouldHidePanelDefault && !isPersonalized
      : shouldHidePanelDefault
    setActivePanel(undefined)

    if (!selectedElement?.element_id || shouldHideRightPanel) {
      dispatch(hideRightPanel({ visibility: 'none' }))
    } else {
      dispatch(hideRightPanel({ visibility: 'collapsed' }))
    }
  }, [
    dispatch,
    selectedElement,
    isOptimized,
    hasVariants,
    isPersonalized,
    flags.showSentimentsMapMockData,
  ])

  return (
    <RightPanel
      collapsedIcons={
        <CollapsedIcons handleActivePanelChange={handleActivePanelChange} />
      }
      onOutsideClick={() => {
        setActivePanel(undefined)
        dispatch(hideRightPanel())
      }}
    >
      {
        {
          variantInspector: (
            <InsightsVariantManager
              activePanel={activePanel}
              isOptimized={isOptimized}
              handleActivePanelChange={handleActivePanelChange}
            />
          ),
          advancedSettings: (
            <ExperimentSettings
              activePanel={activePanel}
              handleActivePanelChange={handleActivePanelChange}
            />
          ),
          insights: (
            <InsightsVariantManager
              activePanel={activePanel}
              isOptimized={isOptimized}
              handleActivePanelChange={handleActivePanelChange}
            />
          ),
          campaignLogs: (
            <CampaignLogs
              expandedIcons={
                <ExpandedIcons
                  activePanel={activePanel}
                  handleActivePanelChange={handleActivePanelChange}
                />
              }
            />
          ),
        }[activePanel ?? '']
      }
    </RightPanel>
  )
}

export default RightSection
